import { Link } from "react-router-dom";
import { ArrowRight, Calculator, CalendarDays, ListChecks, Sparkles, TrendingUp, Sprout } from "lucide-react";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/759e2366-2d5e-409c-a53b-ee028e742e07/images/699533f5c67f34f46ce14e5c44356177130f8adc345817f31f0c6e3cc56f1f5c.png";

const features = [
  {
    icon: TrendingUp,
    title: "Estimate your profit",
    desc: "Run real numbers before the event. Add costs and expected sales, get a clear profit, margin, and break-even.",
    img: "https://images.pexels.com/photos/1151058/pexels-photo-1151058.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    testid: "feature-estimate",
  },
  {
    icon: CalendarDays,
    title: "Plan your booth",
    desc: "Save event details, setup times, products, and items to bring — all in one place for every market.",
    img: "https://images.unsplash.com/photo-1698689972183-08fef0bd1881?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwyfHxmYXJtZXJzJTIwbWFya2V0JTIwdmVuZG9yJTIwYm9vdGh8ZW58MHx8fHwxNzc5ODkxNTQ2fDA&ixlib=rb-4.1.0&q=85",
    testid: "feature-plan",
  },
  {
    icon: ListChecks,
    title: "Stay organized",
    desc: "A complete booth checklist so you never forget the tablecloth, cash bag, or phone charger again.",
    img: "https://images.pexels.com/photos/33593517/pexels-photo-33593517.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    testid: "feature-organize",
  },
];

export default function Landing() {
  return (
    <div className="pb-24 md:pb-12" data-testid="landing-page">
      {/* HERO */}
      <section className="relative">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 sm:pt-12 lg:pt-16">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div className="animate-fade-up">
              <div className="inline-flex items-center gap-2 pop-pill bg-brand-cream-soft text-brand-green border border-brand-line">
                <Sparkles className="w-3.5 h-3.5" strokeWidth={2.25} />
                For vendors, makers & market sellers
              </div>
              <h1 className="font-heading font-semibold mt-5 text-4xl sm:text-5xl lg:text-6xl tracking-tight text-brand-ink leading-[1.05]">
                Know before <br className="hidden sm:block" />
                <span className="text-brand-green">you show.</span>
              </h1>
              <p className="mt-5 text-base sm:text-lg text-brand-mute max-w-xl leading-relaxed">
                PopUp Profit<sup className="text-[10px] ml-0.5">™</sup> is a simple planning tool that helps vendors
                estimate profit, organize booth costs, and prepare for pop-up markets, vendor events, book tables, craft
                fairs, farmers markets, and small business events.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link to="/planner" className="pop-btn-primary" data-testid="cta-start-planning">
                  Start Planning <ArrowRight className="w-4 h-4" strokeWidth={2.25} />
                </Link>
                <Link to="/calculator" className="pop-btn-outline" data-testid="cta-calculate-profit">
                  <Calculator className="w-4 h-4" strokeWidth={2.25} /> Calculate My Profit
                </Link>
              </div>
              <div className="mt-8 flex items-center gap-6 text-xs text-brand-mute">
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-green" /> No signup required
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-gold" /> Free to use
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-mute" /> Saved on your device
                </div>
              </div>
            </div>

            {/* Hero visual */}
            <div className="relative animate-fade-up">
              <div className="relative rounded-3xl overflow-hidden border border-brand-line shadow-[0_30px_80px_-30px_rgba(28,38,28,0.35)]">
                <img src={HERO_IMG} alt="Pop-up market booth" className="w-full h-[420px] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/0 to-transparent" />
                <div className="absolute left-5 right-5 bottom-5 flex items-end justify-between">
                  <div className="text-white">
                    <p className="text-xs uppercase tracking-[0.18em] font-semibold opacity-90">Saturday Market</p>
                    <p className="text-lg font-semibold">Booth #14 · Est. profit $612</p>
                  </div>
                  <div className="bg-white/95 backdrop-blur rounded-2xl px-4 py-3 shadow-lg">
                    <p className="text-[10px] uppercase tracking-wider text-brand-mute font-semibold">Margin</p>
                    <p className="text-xl font-heading font-semibold text-brand-green">38%</p>
                  </div>
                </div>
              </div>
              {/* Floating card */}
              <div className="hidden sm:block absolute -left-6 -bottom-6 pop-card p-4 w-56">
                <p className="label-eyebrow">Break-even</p>
                <p className="font-heading text-2xl font-semibold text-brand-ink mt-1">23 sales</p>
                <p className="text-xs text-brand-mute mt-0.5">at avg $28/item</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT IT HELPS YOU DO */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-20 sm:mt-28">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <p className="label-eyebrow">What PopUp Profit helps you do</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-brand-ink mt-2 tracking-tight">
              Three tools. One clear answer:<br />
              <span className="text-brand-green">is this event worth it?</span>
            </h2>
          </div>
          <Link to="/dashboard" className="text-sm font-medium text-brand-green hover:underline" data-testid="link-dashboard">
            Go to dashboard →
          </Link>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {features.map(({ icon: Icon, title, desc, img, testid }) => (
            <article key={title} className="pop-card pop-card-hover overflow-hidden p-0" data-testid={testid}>
              <div className="aspect-[5/3] overflow-hidden">
                <img src={img} alt={title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="p-5">
                <div className="flex items-center gap-2.5">
                  <span className="w-9 h-9 rounded-xl bg-brand-green/10 text-brand-green grid place-items-center">
                    <Icon className="w-5 h-5" strokeWidth={2} />
                  </span>
                  <h3 className="font-heading text-lg font-semibold text-brand-ink">{title}</h3>
                </div>
                <p className="text-sm text-brand-mute mt-3 leading-relaxed">{desc}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-20 sm:mt-28">
        <div className="relative overflow-hidden rounded-3xl bg-brand-green text-white p-8 sm:p-12">
          <div className="absolute -right-12 -top-12 w-64 h-64 rounded-full bg-brand-gold/20 blur-3xl" />
          <div className="absolute -left-12 -bottom-12 w-72 h-72 rounded-full bg-white/5 blur-3xl" />
          <div className="relative max-w-2xl">
            <Sprout className="w-8 h-8 text-brand-gold" strokeWidth={2} />
            <h3 className="font-heading text-3xl sm:text-4xl font-semibold mt-4 tracking-tight">
              Don't guess. Know before you show.
            </h3>
            <p className="mt-3 text-white/80 text-base leading-relaxed">
              In under 2 minutes, see exactly what an event could earn. Then plan the booth, pack the gear, and walk in
              ready.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/calculator"
                className="inline-flex items-center justify-center gap-2 bg-brand-gold text-brand-ink hover:brightness-95 rounded-full px-6 py-3 font-medium transition-all active:scale-[0.97]"
                data-testid="cta-bottom-calculate"
              >
                <Calculator className="w-4 h-4" strokeWidth={2.25} />
                Run the numbers
              </Link>
              <Link
                to="/checklist"
                className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 text-white border border-white/20 rounded-full px-6 py-3 font-medium transition-all active:scale-[0.97]"
                data-testid="cta-bottom-checklist"
              >
                <ListChecks className="w-4 h-4" strokeWidth={2.25} />
                Open booth checklist
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
